/** How a file is previewed, chosen from its name and type. */
export type FileKind =
  | "image" | "pdf" | "video" | "audio" | "text" | "code" | "csv" | "markdown"
  | "eml" | "msg" | "docx" | "xlsx" | "other";

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;
const BYTES_PER_GB = BYTES_PER_MB * 1024;

/** Largest file the drive accepts (the server refuses bigger ones too). */
export const MAX_FILE_BYTES = 100 * BYTES_PER_MB;

/** The image, video and audio types the server agrees to show inline. */
const INLINE_IMAGE = ["png", "jpg", "jpeg", "gif", "webp", "avif", "bmp"];
const INLINE_VIDEO = ["mp4", "m4v", "webm", "ogv", "mov"];
const INLINE_AUDIO = ["mp3", "ogg", "oga", "wav", "weba", "m4a", "aac", "flac"];
const TEXT = ["txt", "log", "text", "ini", "cfg", "conf", "env", "properties"];
const CODE = [
  "json", "xml", "yaml", "yml", "toml", "html", "htm", "css", "scss", "js", "mjs", "cjs", "jsx", "ts", "tsx",
  "java", "kt", "py", "rb", "go", "rs", "c", "h", "cpp", "hpp", "cs", "php", "sh", "bash", "zsh", "sql",
  "swift", "vue", "svelte", "gradle", "dockerfile", "makefile", "bat", "ps1", "r", "lua", "pl",
];

const BY_EXTENSION: Record<string, FileKind> = {
  pdf: "pdf", csv: "csv", md: "markdown", markdown: "markdown", eml: "eml", msg: "msg", docx: "docx", xlsx: "xlsx",
  ...Object.fromEntries(INLINE_IMAGE.map((x) => [x, "image"])),
  ...Object.fromEntries(INLINE_VIDEO.map((x) => [x, "video"])),
  ...Object.fromEntries(INLINE_AUDIO.map((x) => [x, "audio"])),
  ...Object.fromEntries(TEXT.map((x) => [x, "text"])),
  ...Object.fromEntries(CODE.map((x) => [x, "code"])),
};

/** "report.final.PDF" → "pdf"; names without a dot ("Makefile") use the whole name. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(dot + 1) : name).toLowerCase();
}

/** Types the server sets for files whose extension says nothing to the browser. */
const BY_CONTENT_TYPE: Record<string, FileKind> = {
  "text/plain": "text",
  "text/markdown": "markdown",
  "text/csv": "csv",
  "message/rfc822": "eml",
  "application/vnd.ms-outlook": "msg",
};

/** By extension first, then by the type the server chose. */
export function fileKind(name: string, contentType?: string | null): FileKind {
  return BY_EXTENSION[extensionOf(name)] ?? (contentType ? BY_CONTENT_TYPE[contentType.split(";")[0]?.trim() ?? ""] : undefined) ?? "other";
}

/** A short word for the type, shown on the file's icon: "PDF", "Image", "Sheet"… */
export function kindLabel(name: string): string {
  const ext = extensionOf(name);
  const kind = fileKind(name);
  if (kind === "eml" || kind === "msg") return "Email";
  if (kind === "image") return "Image";
  if (kind === "video") return "Video";
  if (kind === "audio") return "Audio";
  if (["xls", "xlsx", "ods", "csv", "numbers"].includes(ext)) return "Sheet";
  if (["ppt", "pptx", "odp", "key"].includes(ext)) return "Slides";
  if (["doc", "docx", "odt", "rtf", "pages", "md", "markdown", "txt"].includes(ext)) return "Doc";
  if (["zip", "rar", "7z", "gz", "tar"].includes(ext)) return "Archive";
  if (kind === "code") return "Code";
  return ext.length > 0 && ext.length <= 4 && ext !== name.toLowerCase() ? ext.toUpperCase() : "File";
}

/** A friendly name for the type, shown in the viewer for files it can't open. */
export function typeName(name: string): string {
  const label = kindLabel(name);
  return label === "File" ? "File" : `${label} file`;
}

/** "820 B", "14 KB", "2.4 MB", "1.2 GB". */
export function formatSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < BYTES_PER_KB) return `${bytes} B`;
  if (bytes < BYTES_PER_MB) return `${Math.round(bytes / BYTES_PER_KB)} KB`;
  if (bytes < BYTES_PER_GB) return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
  return `${(bytes / BYTES_PER_GB).toFixed(1)} GB`;
}

/** Why a file can't be uploaded, or null when it can. */
export function uploadProblem(file: { size: number }): string | null {
  if (file.size > MAX_FILE_BYTES) return "Files can be up to 100 MB. Split it or compress it first.";
  return null;
}

/** The file's bytes as base64 (without the data: URL prefix). */
export function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}
